import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { imageEditorApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/image-editor/api-schema';
import { imageTagResSchema } from '~/generated/image-editor/zod-schema';

const QUERY_ALL_IMAGE_TAGS_PATH = '/v1/imageTag/queryAllImageTags' as const;

type QueryAllImageTags = typeof QUERY_ALL_IMAGE_TAGS_PATH;

type QueryAllImageTagsResponse = ApiResponse<paths, QueryAllImageTags>;

// NOTE: 使用者自訂，所以沒 i18n
const queryAllImageTagsResponseSchema = z.array(
  zodRemoveNullable(imageTagResSchema),
);

export type QueryAllImageTagsData = z.infer<
  typeof queryAllImageTagsResponseSchema
>;

const queryFn = async (signal?: AbortSignal) => {
  const { data: response } =
    await imageEditorApi.get<QueryAllImageTagsResponse>(
      QUERY_ALL_IMAGE_TAGS_PATH,
      { signal },
    );

  const result = queryAllImageTagsResponseSchema.safeParse(response.data);
  if (!result.success) {
    throw new Error(result.error.message);
  }

  return result.data;
};

export const getSuspenseQueryAllImageTagsOptions = () => {
  return queryOptions({
    queryKey: [QUERY_ALL_IMAGE_TAGS_PATH] as const,
    queryFn: ({ signal }) => queryFn(signal),
  });
};

export const useQueryAllImageTags = (
  options?: QueryOptions<QueryAllImageTagsData>,
) => {
  return useQuery({
    queryKey: [QUERY_ALL_IMAGE_TAGS_PATH],
    queryFn: async ({ signal }) => await queryFn(signal),
    ...options,
  });
};

export { QUERY_ALL_IMAGE_TAGS_PATH as QUERY_ALL_IMAGE_TAGS_KEY };
